import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileExcel,
  faFilePdf,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import CgDialog from 'modules/Common/ui/CgDialog';
import { loadXlsx } from 'modules/Common/utils/loadXlsx';

const FormatButton = ({ icon, title, extension, onClick, disabled, soonBadge, loading }) => (
  <button
    type="button"
    onClick={disabled || loading ? undefined : onClick}
    disabled={disabled || loading}
    aria-busy={loading || undefined}
    className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors
      ${
        disabled || loading
          ? 'border-slate-200 text-slate-400 bg-white cursor-not-allowed'
          : 'border-slate-300 text-slate-700 bg-white hover:bg-slate-50 cursor-pointer'
      }
    `}
  >
    <FontAwesomeIcon
      icon={loading ? faSpinner : icon}
      spin={loading || undefined}
      className="text-sm"
      style={
        !disabled && !loading && icon === faFileExcel
          ? { color: 'rgb(var(--primary-color))' }
          : undefined
      }
    />
    <span>{title}</span>
    {extension && <span className="text-xs font-normal text-slate-400">{extension}</span>}
    {soonBadge && (
      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500">
        Soon
      </span>
    )}
  </button>
);

/**
 * Download modal triggered by the header's "Download data" button — the Excel
 * export and the full CBAM report PDF.
 *
 * Download-only on purpose: the supplier-invite pitch it used to sit under now
 * lives in its own RequestSupplierDataModal behind a separate header button, so
 * a visitor who came for the file gets it in one click (CG-2359).
 *
 * The PDF (CG-2362) is rendered in the visitor's browser by the same component
 * the app uses (`CbamStock/components/LotsEmissionsReportPDF`), so it is the
 * same document a logged-in user would generate. Rendering takes a moment, so
 * the modal stays OPEN and reports progress in place rather than closing on
 * click (Excel likewise stays open with its button busy until the file is built). The tile keeps its `Soon` badge until the portal payload
 * reports `reportPdfAvailable`, so we never offer a download with nothing
 * behind it.
 */
export default function DownloadDataModal({
  open,
  onOpenChange,
  operatorName,
  onDownloadExcel,
  pdfAvailable = false,
  onDownloadPdf,
  pdfPending = false,
  pdfError = null,
}) {
  const close = () => onOpenChange?.(false);
  // Stay open with the Excel button busy until the file is built: the first
  // export in a session also downloads xlsx, and closing first left nothing on
  // screen during that wait.
  const [excelPending, setExcelPending] = useState(false);

  // Warm the xlsx chunk while the visitor is still reading the two tiles, so
  // the click itself downloads nothing and stays as instant as it was before
  // xlsx moved behind `loadXlsx`. `loadXlsx` memoises, so reopening is free,
  // and a rejection here is swallowed on purpose: nothing is downloading yet,
  // so the click is still the place that reports a real failure.
  useEffect(() => {
    if (!open) return;
    loadXlsx().catch(() => {});
  }, [open]);

  const handleExcel = async () => {
    setExcelPending(true);
    try {
      await onDownloadExcel?.();
    } finally {
      setExcelPending(false);
      close();
    }
  };

  return (
    <CgDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Download data"
      description={`Grab ${operatorName || 'this supplier'}'s emissions data in your preferred format.`}
      width={616}
    >
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <FormatButton
            icon={faFileExcel}
            title="Excel spreadsheet"
            extension=".xlsx"
            onClick={handleExcel}
            loading={excelPending}
            // One download at a time: Excel closes the dialog when it finishes,
            // which would hide a PDF still rendering.
            disabled={pdfPending}
          />
          <FormatButton
            icon={faFilePdf}
            title="PDF report"
            extension={pdfAvailable ? '.pdf' : undefined}
            onClick={onDownloadPdf}
            loading={pdfPending}
            disabled={!pdfAvailable || excelPending}
            soonBadge={!pdfAvailable}
          />
        </div>

        {pdfPending && (
          <p className="text-xs text-slate-500 m-0 text-center">
            Building the emissions report — keep this window open.
          </p>
        )}

        {pdfError && !pdfPending && (
          <p className="text-xs text-red-600 m-0 flex items-center justify-center gap-1.5">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="text-[10px]"
            />
            {pdfError}
          </p>
        )}
      </div>
    </CgDialog>
  );
}
